import { useEffect, useState, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MapPin } from 'lucide-react';

interface AddressPreviewMapProps {
  latitude?: number;
  longitude?: number;
  displayName?: string;
  onPositionChange?: (lat: number, lng: number) => void;
}

export const AddressPreviewMap = ({ 
  latitude, 
  longitude, 
  displayName,
  onPositionChange 
}: AddressPreviewMapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const marker = useRef<maplibregl.Marker | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const hasValidCoordinates = 
    isMounted && 
    latitude !== undefined && 
    latitude !== null && 
    longitude !== undefined && 
    longitude !== null &&
    !isNaN(latitude) &&
    !isNaN(longitude) &&
    latitude >= -90 && latitude <= 90 &&
    longitude >= -180 && longitude <= 180;

  useEffect(() => {
    if (!hasValidCoordinates || !mapContainer.current) return;

    // Initialize map
    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        sources: {
          osm: {
            type: 'raster',
            tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
            tileSize: 256,
          }
        },
        layers: [{
          id: 'osm',
          type: 'raster',
          source: 'osm',
          minzoom: 0,
          maxzoom: 19
        }]
      },
      center: [longitude!, latitude!],
      zoom: 15
    });

    // Create custom marker element
    const el = document.createElement('div');
    el.className = 'custom-marker';
    el.style.cursor = 'move';
    el.innerHTML = `
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 2L24 10V26L16 30L8 26V10L16 2Z" fill="#FBBF24" stroke="#000" stroke-width="3"/>
        <circle cx="16" cy="14" r="4" fill="#000"/>
        <path d="M16 18L16 26" stroke="#000" stroke-width="3" stroke-linecap="square"/>
      </svg>
    `;

    // Create draggable marker
    marker.current = new maplibregl.Marker({
      element: el,
      draggable: true
    })
      .setLngLat([longitude!, latitude!])
      .addTo(map.current);

    // Popup content
    const popupContent = `
      <div style="padding: 8px;">
        ${displayName ? `<p style="font-weight: 600; margin-bottom: 4px;">${displayName}</p>` : ''}
        <p style="font-size: 12px; color: #666; margin-bottom: 8px;">
          📍 Trascina il pin per posizionarlo esattamente
        </p>
        <p style="font-size: 10px; font-family: monospace;">
          ${latitude!.toFixed(6)}, ${longitude!.toFixed(6)}
        </p>
      </div>
    `;

    const popup = new maplibregl.Popup({ offset: 25 })
      .setHTML(popupContent);

    marker.current.setPopup(popup);

    // Handle drag end
    marker.current.on('dragend', () => {
      const lngLat = marker.current!.getLngLat();
      onPositionChange?.(lngLat.lat, lngLat.lng);
      
      // Update popup content with new coordinates
      const newPopupContent = `
        <div style="padding: 8px;">
          ${displayName ? `<p style="font-weight: 600; margin-bottom: 4px;">${displayName}</p>` : ''}
          <p style="font-size: 12px; color: #666; margin-bottom: 8px;">
            📍 Trascina il pin per posizionarlo esattamente
          </p>
          <p style="font-size: 10px; font-family: monospace;">
            ${lngLat.lat.toFixed(6)}, ${lngLat.lng.toFixed(6)}
          </p>
        </div>
      `;
      marker.current!.getPopup().setHTML(newPopupContent);
      
      console.log('📍 Pin spostato:', { lat: lngLat.lat, lng: lngLat.lng });
    });

    // Cleanup
    return () => {
      marker.current?.remove();
      map.current?.remove();
    };
  }, [hasValidCoordinates, latitude, longitude, displayName, onPositionChange]);

  if (!hasValidCoordinates) {
    return (
      <div className="w-full h-[200px] bg-muted border-[3px] border-dark flex items-center justify-center">
        <div className="text-center text-muted-foreground">
          <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Verifica l'indirizzo per vedere la mappa</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={mapContainer} 
      className="w-full h-[200px] border-[3px] border-dark shadow-[4px_4px_0px_#101322]"
    />
  );
};
